import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  downloadEnrollmentsCsv,
  downloadEnrollmentsPdf,
  downloadGradesCsv,
  downloadGradesPdf,
  downloadStudentsCsv,
  downloadStudentsPdf,
  downloadSubmissionsCsv,
  downloadSubmissionsPdf,
  getAdminDashboard,
} from '@/lib/api';
import {
  ArrowRight,
  Award,
  BookOpen,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Printer,
  Users,
} from 'lucide-react';

const reports = [
  {
    id: 'enrollments',
    title: 'Program Enrollments',
    description: 'Current student program enrollments with program, intake start/end dates, and enrollment status.',
    icon: Users,
    downloads: [
      { format: 'CSV', icon: FileSpreadsheet, action: downloadEnrollmentsCsv },
      { format: 'PDF', icon: Printer, action: downloadEnrollmentsPdf },
    ],
  },
  {
    id: 'grades',
    title: 'Course Grades',
    description: 'Grade book records from the grades table with course, term, credits, and status.',
    icon: Award,
    downloads: [
      { format: 'CSV', icon: FileSpreadsheet, action: downloadGradesCsv },
      { format: 'PDF', icon: Printer, action: downloadGradesPdf },
    ],
  },
  {
    id: 'submissions',
    title: 'Submissions',
    description: 'Student assessment submissions enriched with course, assessment, score, and grading status.',
    icon: FileText,
    downloads: [
      { format: 'CSV', icon: FileSpreadsheet, action: downloadSubmissionsCsv },
      { format: 'PDF', icon: Printer, action: downloadSubmissionsPdf },
    ],
  },
  {
    id: 'students',
    title: 'Students',
    description: 'Full student records export for spreadsheet review or printable internal files.',
    icon: FileSpreadsheet,
    downloads: [
      { format: 'CSV', icon: FileSpreadsheet, action: downloadStudentsCsv },
      { format: 'PDF', icon: Printer, action: downloadStudentsPdf },
    ],
  },
];

const summaryCards = [
  { key: 'total_students', label: 'Students', icon: Users },
  { key: 'total_courses', label: 'Courses', icon: BookOpen },
  { key: 'total_submissions', label: 'Submissions', icon: FileText },
  { key: 'pending_grading', label: 'Pending Grading', icon: Award },
];

export default function AdminReports() {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      try {
        setStatsLoading(true);
        const { data } = await getAdminDashboard();
        if (!cancelled) {
          setStats(data?.stats || null);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setStats(null);
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
        }
      }
    };

    loadStats();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownload = async (report, download) => {
    const downloadKey = `${report.id}-${download.format.toLowerCase()}`;
    try {
      setDownloading(downloadKey);
      await download.action();
      toast.success(`${report.title} ${download.format} downloaded`);
    } catch (err) {
      console.error(err);
      toast.error(`Could not download ${report.title.toLowerCase()} ${download.format}`);
    } finally {
      setDownloading('');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Reports
          </h2>
          <p className="text-sm text-[#062B1A]/60 mt-1">
            Download current operational reports as CSV or PDF from one admin hub
          </p>
        </div>
        <Button asChild variant="outline" className="border-[#062B1A]/15 text-[#062B1A] hover:bg-[#062B1A]/5">
          <Link to="/admin/analytics">
            View analytics
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {summaryCards.map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.key} className="card-hover border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">
                    {item.label}
                  </span>
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                  {statsLoading ? '...' : stats?.[item.key] ?? 0}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {reports.map((report) => {
          const Icon = report.icon;
          return (
            <Card key={report.id} className="card-hover border border-[rgba(6,43,26,0.08)] shadow-sm overflow-hidden">
              <div className="h-1.5 bg-[#062B1A]" />
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#062B1A] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                        {report.title}
                      </CardTitle>
                      <Badge variant="outline" className="mt-1 border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#062B1A] hover:bg-[#D4AF37]/10">
                        CSV + PDF
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="min-h-[3rem] text-sm text-[#062B1A]/60 leading-relaxed">{report.description}</p>
                <div className="mt-5 grid grid-cols-2 gap-2">
                  {report.downloads.map((download) => {
                    const DownloadIcon = download.icon || Download;
                    const downloadKey = `${report.id}-${download.format.toLowerCase()}`;
                    const isDownloading = downloading === downloadKey;
                    return (
                      <Button
                        key={download.format}
                        className={download.format === 'PDF'
                          ? 'bg-[#062B1A] text-white hover:bg-[#041B10] btn-hover'
                          : 'border-[#062B1A]/15 text-[#062B1A] hover:bg-[#062B1A]/5'}
                        variant={download.format === 'PDF' ? 'default' : 'outline'}
                        onClick={() => handleDownload(report, download)}
                        disabled={Boolean(downloading)}
                        data-testid={`download-${report.id}-${download.format.toLowerCase()}`}
                      >
                        {isDownloading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <DownloadIcon className="w-4 h-4 mr-2" />
                        )}
                        {download.format}
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#062B1A]">About report downloads</p>
              <p className="text-xs text-[#062B1A]/50 mt-1 leading-relaxed">
                Reports are generated from the current database and downloaded with your admin session.
                Charts and trend exploration live on the Analytics page.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
